import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Select, DatePicker } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import styles from "./installer-vendor-roles.module.scss";

interface InstallerVendorRole {
  id: string;
  roleName: string;
  description: string;
  permissions: string[];
  accessAreas: string[];
  users: number;
  vendorType: 'installer';
  active: boolean;
  createdBy: string;
  createdAt: string;
}

const InstallerVendorRoles: React.FC = () => {
  const navigate = useNavigate();
  // Filter state
  const [filters, setFilters] = useState({
    roleName: undefined as string | undefined,
    active: undefined as string | undefined,
    users: undefined as string | undefined,
  });
  // Reset counter for forcing re-render of components
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleData, setNewRoleData] = useState({
    roleName: '',
    description: '',
    permissions: [] as string[]
  });

  // Mock data
  const installerVendorRoles: InstallerVendorRole[] = [
    {
      id: 'installer-supervisor',
      roleName: 'Installation Supervisor',
      description: 'Can supervise and manage all installation activities',
      permissions: ['View Installations', 'Update Status', 'Manage Users', 'View Reports', 'Manage Inventory'],
      accessAreas: ['dashboard', 'installations', 'tracking', 'reports', 'inventory', 'users'],
      users: 1,
      vendorType: 'installer',
      active: true,
      createdBy: 'System Admin',
      createdAt: '2024-01-10'
    },
    {
      id: 'installer-technician',
      roleName: 'Installation Technician',
      description: 'Can manage installations and device deployments',
      permissions: ['View Installations', 'Update Status', 'View Tracking'],
      accessAreas: ['dashboard', 'installations', 'tracking'],
      users: 4,
      vendorType: 'installer',
      active: true,
      createdBy: 'Installation Supervisor',
      createdAt: '2024-01-15'
    },
    {
      id: 'installer-manager',
      roleName: 'Installation Manager',
      description: 'Can manage installation projects and team coordination',
      permissions: ['View Installations', 'Update Status', 'View Reports', 'Manage Inventory'],
      accessAreas: ['dashboard', 'installations', 'reports', 'inventory'],
      users: 2,
      vendorType: 'installer',
      active: true,
      createdBy: 'Installation Supervisor',
      createdAt: '2024-01-20'
    }
  ];

  // Generate filter options dynamically
  const filterOptions = {
    roleNames: installerVendorRoles.map(role => role.roleName),
    active: ['Active', 'Inactive'],
    users: ['0', '1-5', '6-10', '11+']
  };

  // Filter roles based on search terms
  const filteredRoles = installerVendorRoles.filter(role => {
    return (
      (!filters.roleName || role.roleName === filters.roleName) &&
      (!filters.active || (filters.active === 'Active' ? role.active : !role.active)) &&
      (!filters.users || 
        (filters.users === '0' && role.users === 0) ||
        (filters.users === '1-5' && role.users >= 1 && role.users <= 5) ||
        (filters.users === '6-10' && role.users >= 6 && role.users <= 10) ||
        (filters.users === '11+' && role.users >= 11)
      )
    );
  });
  
  const totalPages = 1; // Since we only have a few test roles

  // Filter change
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ 
      roleName: undefined, 
      active: undefined, 
      users: undefined 
    });
    setResetCounter(prev => prev + 1);
  };

  const handleOnClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleOnClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleAddRole = () => {
    console.log('Adding new role:', newRoleData);
    // In real app, this would make an API call
    setShowAddRoleModal(false);
    setNewRoleData({
      roleName: '',
      description: '',
      permissions: []
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setNewRoleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Installer Vendor Roles" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Role Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`roleName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any role name"
                            value={filters.roleName}
                            onChange={value => handleFilterChange('roleName', value)}
                            options={filterOptions.roleNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`active-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.active}
                            onChange={value => handleFilterChange('active', value)}
                            options={filterOptions.active.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Users"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`users-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any user count"
                            value={filters.users}
                            onChange={value => handleFilterChange('users', value)}
                            options={filterOptions.users.map(count => ({ label: count, value: count }))}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}></Col>
                      <Col md={4}></Col>
                      <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
                        <Button
                          className="w-100"
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px'
                          }}
                          onClick={() => setShowAddRoleModal(true)}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add Role
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
          
          {/* Installer Vendor Roles Section */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Installer Vendor Roles ({filteredRoles.length})</h3>
            </div>
            
            {/* Installer Vendor Roles Table */}
            <Table hover responsive className="legacy-row-height-table">
              <thead>
                <tr>
                  <th>Role Name</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Access Areas</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((role) => (
                  <tr key={role.id}>
                    <td>{role.roleName}</td>
                    <td>{role.description}</td>
                    <td>
                      <div className={styles.permissionsList}>
                        {role.permissions.map((permission, index) => (
                          <span key={index} className={styles.permissionBadge}>
                            {permission}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className={styles.permissionsList}>
                        {role.accessAreas.map((area, index) => (
                          <span key={index} className={styles.permissionBadge}>
                            {area}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{role.users}</td>
                    <td>
                      <span className={`badge ${role.active ? 'bg-success' : 'bg-secondary'}`}>
                        {role.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{role.createdBy}</td>
                    <td>{role.createdAt}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button 
                          variant="link" 
                          className={styles.actionLink}
                          onClick={() => console.log('Edit role:', role.id)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="link" 
                          className={styles.actionLink}
                          onClick={() => console.log('View role:', role.id)}
                        >
                          View
                        </Button>
                        <Button 
                          variant="link" 
                          className={styles.deleteLink}
                          onClick={() => console.log('Delete role:', role.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          <PaginationRow
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={typeof totalPages === "number" ? totalPages : undefined}
            onClickPrevious={handleOnClickPrev}
            handleOnClickNext={handleOnClickNext}
            disableLeft={page === 1}
            disableNext={page === totalPages}
          />
        </Card.Body>
      </Card>

      {/* Add Role Modal */}
      <Modal show={showAddRoleModal} onHide={() => setShowAddRoleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Installer Vendor Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter role name"
                    value={newRoleData.roleName}
                    onChange={(e) => handleInputChange('roleName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter role description"
                    value={newRoleData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddRoleModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddRole}
            disabled={!newRoleData.roleName || !newRoleData.description}
          >
            Add Role
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(InstallerVendorRoles);
